import Base from '../base/base';
import { LEADS_API } from '../../shared/utils/variables';
import './buyer-intake-form.scss';

// ============================================
// TYPES
// ============================================

interface TextFieldConfig {
  type: 'text' | 'email' | 'tel';
  name: string;
  placeholder: string;
  required: boolean;
  hint?: string;
}

interface TextareaFieldConfig {
  type: 'textarea';
  name: string;
  placeholder: string;
  required: boolean;
}

interface RadioOption {
  value: string;
  label: string;
  reveals?: FieldConfig[];
}

interface RadioFieldConfig {
  type: 'radio';
  name: string;
  question: string;
  required: boolean;
  options: RadioOption[];
}

interface CheckboxFieldConfig {
  type: 'checkbox';
  name: string;
  label: string;
}

type FieldConfig = TextFieldConfig | TextareaFieldConfig | RadioFieldConfig | CheckboxFieldConfig;

interface ScreenConfig {
  id: string;
  title: string;
  subtitle?: string;
  userTypes?: string[]; // If undefined, screen is shown for all user types
  fields: FieldConfig[];
}

interface FormConfig {
  screens: ScreenConfig[];
}

interface BuyerIntakeFormOptions {
  debug?: boolean;
  containerId?: string;
  containerElement?: HTMLElement;
  containerSelector?: string;
  initAll?: boolean;
  /**
   * Replaces the first screen's title, whichever flow the container is in.
   * Later screens keep their own. Outranks every internal default, so a page
   * that sets nothing behaves exactly as before.
   */
  title?: string;
  /** Replaces the first screen's subtitle, on the same terms as `title` */
  subtitle?: string;
  /**
   * Tags the lead's origin in the submitted payload. Supplied by the page,
   * rather than derived from `data-context`, so a page can describe itself.
   */
  userIntent?: string;
}

interface FormData {
  // Shared fields
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  userType: string;
  /** Which page the lead came through; only set for contexts that define one */
  userIntent?: string;
  referral: string;
  referralOther?: string;
  vision?: string;
  newsletter?: boolean;
  /** The page path the form was submitted on */
  urlPath?: string;

  // Homebuyer fields
  financing?: string;
  landOwnership?: string;
  timeline?: string;
  budget?: string;

  // Developer fields
  homeCount?: string;
  projectStage?: string;

  // Listing inquiry fields
  landAddress?: {
    location: {
      lat: number;
      lng: number;
    };
    formatted_address: string;
  };
}

const API_ENDPOINT = LEADS_API;

// ============================================
// FORM CONFIGURATION
// ============================================


const FORM_CONFIG: FormConfig = {
  screens: [
    // Screen: Contact Details (users who need to select their type)
    {
      id: 'contact',
      title: 'Contact Details',
      subtitle: 'Tell us a bit about yourself.',
      userTypes: ['homebuyer', 'developer', 'general-interest'],
      fields: [
        { type: 'text', name: 'firstName', placeholder: 'First Name', required: true },
        { type: 'text', name: 'lastName', placeholder: 'Last Name', required: true },
        {
          type: 'email',
          name: 'email',
          placeholder: 'Email Address',
          required: true
        },
        {
          type: 'tel',
          name: 'phone',
          placeholder: 'Phone Number',
          required: true
        },
        {
          type: 'radio',
          name: 'userType',
          question: 'What best describes you?',
          required: true,
          options: [
            { value: 'homebuyer', label: 'Homebuyer' },
            { value: 'developer', label: 'Developer / Organization' },
            { value: 'general-interest', label: 'General Interest' },
          ],
        },
      ],
    },

    // ========== HOMEBUYER SCREENS ==========

    // Screen: Financing & Land (HOMEBUYER)
    {
      id: 'hb-financing',
      title: 'Financing & Land',
      userTypes: ['homebuyer'],
      fields: [
        {
          type: 'radio',
          name: 'financing',
          question: 'How are you financing the build?',
          required: true,
          options: [
            { value: 'self-funding', label: "I'll be self-funding the build" },
            { value: 'construction-loan', label: 'I need a construction loan' },
          ],
        },
        {
          type: 'radio',
          name: 'landOwnership',
          question: 'Do you already own land?',
          required: true,
          options: [
            { value: 'yes', label: 'Yes, I own land' },
            { value: 'no', label: 'No, I need to purchase land' },
          ],
        },
      ],
    },

    // Screen: Project Timeline (HOMEBUYER)
    {
      id: 'hb-timeline',
      title: 'Project Timeline',
      userTypes: ['homebuyer'],
      fields: [
        {
          type: 'radio',
          name: 'timeline',
          question: 'When do you want to start building?',
          required: true,
          options: [
            { value: 'less-than-3', label: 'Less than 3 months' },
            { value: '3-to-6', label: '3 to 6 months' },
            { value: '6-to-12', label: '6 to 12 months' },
            { value: 'more-than-12', label: '12+ months' },
          ],
        },
      ],
    },

    // Screen: Estimated Costs (HOMEBUYER)
    {
      id: 'hb-budget',
      title: 'Estimated Costs',
      userTypes: ['homebuyer'],
      fields: [
        {
          type: 'radio',
          name: 'budget',
          question: 'What do you expect to spend on your home build?',
          required: true,
          options: [
            { value: '100k-200k', label: '$100,000 – $200,000' },
            { value: '200k-300k', label: '$200,000 – $300,000' },
            { value: '300k-400k', label: '$300,000 – $400,000' },
            { value: '400k-500k', label: '$400,000 – $500,000' },
            { value: '500k-plus', label: '$500,000+' },
          ],
        },
      ],
    },

    // ========== DEVELOPER SCREENS ==========

    // Screen: Project Scope (DEVELOPER)
    {
      id: 'dev-scope',
      title: 'Project Scope',
      userTypes: ['developer'],
      fields: [
        {
          type: 'radio',
          name: 'homeCount',
          question: 'How many homes/units are you planning to build?',
          required: true,
          options: [
            { value: '1', label: 'Just 1' },
            { value: '2-5', label: '2–5 homes' },
            { value: '6-20', label: '6–20 homes' },
            { value: '20+', label: '20+ homes' },
          ],
        },
      ],
    },

    // Screen: Stage & Site (DEVELOPER)
    {
      id: 'dev-stage',
      title: 'Stage & Site',
      userTypes: ['developer'],
      fields: [
        {
          type: 'radio',
          name: 'projectStage',
          question: 'What stage are you currently in?',
          required: true,
          options: [
            { value: 'early-exploration', label: 'Early exploration' },
            { value: 'planning', label: 'Planning a project' },
            { value: 'ready-to-build', label: 'Ready to build / already building' },
          ],
        },
        {
          type: 'radio',
          name: 'landOwnership',
          question: 'Do you already own land?',
          required: true,
          options: [
            { value: 'yes', label: 'Yes, I own land' },
            { value: 'no', label: 'No, I need to purchase land' },
          ],
        },
      ],
    },

    // Screen: Build Timeline (DEVELOPER)
    {
      id: 'dev-timeline',
      title: 'Build Timeline',
      userTypes: ['developer'],
      fields: [
        {
          type: 'radio',
          name: 'timeline',
          question: 'When do you want to start building?',
          required: true,
          options: [
            { value: 'less-than-3', label: 'Less than 3 months' },
            { value: '3-to-6', label: '3 to 6 months' },
            { value: '6-to-12', label: '6 to 12 months' },
            { value: 'more-than-12', label: '12+ months' },
          ],
        },
      ],
    },

    // Screen: Project Details (DEVELOPER)
    {
      id: 'dev-details',
      title: 'Project Details',
      userTypes: ['developer'],
      fields: [
        {
          type: 'textarea',
          name: 'vision',
          placeholder: 'Tell us a bit about your project.',
          required: false,
        },
      ],
    },

    // ========== GENERAL INTEREST SCREENS ==========

    // Screen: Have Questions? (GENERAL INTEREST)
    {
      id: 'gi-questions',
      title: 'Have Questions?',
      userTypes: ['general-interest'],
      fields: [
        {
          type: 'textarea',
          name: 'vision',
          placeholder: 'Let us know how we can assist.',
          required: false,
        },
      ],
    },

    // ========== LISTING INQUIRY SCREENS ==========

    // Screen: Contact Details (LISTING INQUIRY - no userType selector)
    {
      id: 'li-contact',
      title: 'Interested in This Property?',
      subtitle: "We'll be in touch shortly.",
      userTypes: ['listing-inquiry'],
      fields: [
        { type: 'text', name: 'firstName', placeholder: 'First Name', required: true },
        { type: 'text', name: 'lastName', placeholder: 'Last Name', required: true },
        {
          type: 'email',
          name: 'email',
          placeholder: 'Email Address',
          required: true
        },
        {
          type: 'tel',
          name: 'phone',
          placeholder: 'Phone Number',
          required: true
        },
      ],
    },

    // Screen: Questions about this property (LISTING INQUIRY)
    {
      id: 'li-questions',
      title: 'Almost Done',
      subtitle: 'Any questions about this property?',
      userTypes: ['listing-inquiry'],
      fields: [
        {
          type: 'textarea',
          name: 'vision',
          placeholder: 'Anything else you want us to know?',
          required: false,
        },
      ],
    },

    // ========== SHARED FINAL SCREEN ==========

    // Screen: Almost Done (ALL users)
    {
      id: 'finish',
      title: 'Almost Done',
      fields: [
        {
          type: 'radio',
          name: 'referral',
          question: 'Where did you hear about us?',
          required: true,
          options: [
            { value: 'ritz-craft', label: 'Ritz-Craft referral' },
            { value: 'social-media', label: 'Social media' },
            { value: 'friend-family', label: 'From a friend or family member' },
            { value: 'local-event', label: 'Local event or open house' },
            { value: 'google-search', label: 'Google / online search' },
            {
              value: 'other',
              label: 'Other',
              // reveals: [
              //   { type: 'text', name: 'referralOther', placeholder: 'Please specify', required: true },
              // ],
            },
          ],
        },
        { type: 'checkbox', name: 'newsletter', label: 'Join our newsletter' },
      ],
    },
  ],
};

// ============================================
// TOUR CONTEXT
// ============================================

/**
 * Applied when the container carries `data-context="tour"`.
 *
 * The title and subtitle replace the first screen's heading only; later
 * screens keep their own. The screen flow is untouched — tour context does
 * not set a userType, so the picker still drives which screens show.
 */
const TOUR_CONTEXT = {
  title: 'Book a Tour',
  subtitle:
    "Tell us a little about yourself, and we'll be in touch to coordinate a convenient time for you to tour the home.",
  userIntent: 'model-tour',
};

// ============================================
// COMPONENT CLASS
// ============================================

class BuyerIntakeForm extends Base {
  private container: HTMLElement | null = null;
  private formElement: HTMLFormElement | null = null;
  private config: FormConfig;
  private containerId: string;
  private containerElement: HTMLElement | null = null;
  private containerSelector: string;
  private isStackedLayout: boolean = false;
  private isTourContext: boolean = false;
  private currentScreenIndex: number = 0;
  private activeScreenIds: string[] = [];
  private selectedUserType: string | null = null;
  private listingAddress: string = '';
  private listingLat: number | null = null;
  private listingLng: number | null = null;
  /** Page-supplied first-screen copy and lead origin; null means "use the default" */
  private titleOverride: string | null = null;
  private subtitleOverride: string | null = null;
  private userIntent: string | null = null;

  constructor(options: BuyerIntakeFormOptions = {}) {
    super(options.debug || false);
    this.debug = options.debug || false;
    this.config = FORM_CONFIG;
    this.titleOverride = options.title ?? null;
    this.subtitleOverride = options.subtitle ?? null;
    this.userIntent = options.userIntent ?? null;
    this.containerId = options.containerId || 'buyer-intake-form';
    this.containerElement = options.containerElement || null;
    this.containerSelector = options.containerSelector || '.buyer-intake-wrapper';

    if (!this.containerElement && options.initAll !== false) {
      const containers = Array.from(document.querySelectorAll<HTMLElement>(this.containerSelector));

      if (containers.length > 1) {
        containers.forEach((element) => {
          // eslint-disable-next-line no-new
          new BuyerIntakeForm({
            ...options,
            containerElement: element,
            initAll: false,
          });
        });
        return;
      }

      if (containers.length === 1) {
        this.containerElement = containers[0];
      }
    }

    this.init();
  }

  init(): void {
    this.log('BuyerIntakeForm initializing...');

    this.container = this.containerElement || document.getElementById(this.containerId);

    if (!this.container) {
      this.log(`Container #${this.containerId} not found, aborting initialization`);
      return;
    }

    // Check for context from data attributes; listing context also sets userType
    const context = this.container.dataset.context;
    const isListingContext = context === 'listing';
    this.isTourContext = context === 'tour';
    this.listingAddress = this.container.dataset.listingAddress || '';
    this.listingLat = this.parseCoordinate(this.container.dataset.lat);
    this.listingLng = this.parseCoordinate(
      this.container.dataset.lng || this.container.dataset.long
    );
    this.isStackedLayout = this.container.dataset.layout === 'stacked';

    if (isListingContext) {
      this.selectedUserType = 'listing-inquiry';
      this.log(
        `Listing context detected, userType set to: ${this.selectedUserType}, address: ${this.listingAddress}, lat: ${this.listingLat}, lng: ${this.listingLng}`
      );
    }

    this.render();
    this.bindEvents();
    
    // Initialize active screens based on selectedUserType
    if (this.selectedUserType) {
      // UserType already known, get the full flow
      this.activeScreenIds = this.getActiveScreenIds();
    } else {
      // UserType not yet selected, start with contact screen
      this.activeScreenIds = ['contact'];
    }
    
    this.updateProgress();
    this.showScreen(this.activeScreenIds[0]);
    this.updateSidebar();

    this.log('BuyerIntakeForm initialized');
  }

  // ============================================
  // ACTIVE SCREENS
  // ============================================

  private parseCoordinate(value?: string): number | null {
    if (!value) return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getActiveScreenIds(): string[] {
    // Get userType from stored selection or from radio button
    let userType = this.selectedUserType;
    
    if (!userType) {
      const userTypeRadio = this.container?.querySelector<HTMLInputElement>(
        'input[name="userType"]:checked'
      );
      userType = userTypeRadio?.value || '';
    }

    // Filter screens based on userType
    return this.config.screens
      .filter((screen) => {
        // If no userTypes specified, screen is for all users (like 'finish')
        if (!screen.userTypes) return true;
        // Otherwise, check if current userType is in the array
        return screen.userTypes.includes(userType);
      })
      .map((screen) => screen.id);
  }

  private getCurrentScreenId(): string {
    return this.activeScreenIds[this.currentScreenIndex] || 'contact';
  }

  private getScreenConfig(screenId: string): ScreenConfig | undefined {
    return this.config.screens.find((s) => s.id === screenId);
  }

  // ============================================
  // EVENT BINDING
  // ============================================

  private bindEvents(): void {
    if (!this.container) return;

    this.formElement = this.container.querySelector('.bif-form');

    // Next button clicks
    this.container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      if (target.closest('.bif-btn-next')) {
        e.preventDefault();
        this.handleNext();
      }

      if (target.closest('.bif-back')) {
        e.preventDefault();
        this.handleBack();
      }
    });

    // Radio change for selected state (JS fallback for :has)
    this.container.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;

      if (target.classList.contains('bif-radio-input')) {
        this.handleRadioChange(target);
      }
    });

    // Clear invalid state on text input
    this.container.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;

      if (target.classList.contains('bif-input') || target.classList.contains('bif-textarea')) {
        target.classList.remove('is-invalid');
      }
    });

    this.log('Events bound');
  }

  // ============================================
  // NAVIGATION
  // ============================================

  private handleNext(): void {
    const currentScreenId = this.getCurrentScreenId();
    this.log(`handleNext called, currentScreenIndex: ${this.currentScreenIndex}, screenId: ${currentScreenId}`);

    // Validate current screen
    if (!this.validateCurrentScreen()) {
      this.log('Validation failed');
      return;
    }

    // After first screen (contact), recalculate active screens based on userType
    if (currentScreenId === 'contact') {
      this.activeScreenIds = this.getActiveScreenIds();
      this.log('Active screens updated:', this.activeScreenIds);
    }

    // If we're on the last screen, submit
    if (this.currentScreenIndex === this.activeScreenIds.length - 1) {
      this.log('Last screen, triggering submit');
      this.handleSubmit();
      return;
    }

    // Move to next screen
    this.currentScreenIndex++;
    this.showScreen(this.getCurrentScreenId());
    this.updateProgress();
    this.updateSidebar();
  }

  private handleBack(): void {
    this.log(`handleBack called, currentScreenIndex: ${this.currentScreenIndex}`);

    if (this.currentScreenIndex <= 0) return;

    this.currentScreenIndex--;
    this.showScreen(this.getCurrentScreenId());
    this.updateProgress();
    this.updateSidebar();
  }

  private showScreen(screenId: string): void {
    if (!this.container) return;

    const allScreens = this.container.querySelectorAll('.bif-screen');
    allScreens.forEach((screen) => screen.classList.remove('is-active'));

    const targetScreen = this.container.querySelector(`[data-screen="${screenId}"]`);
    if (targetScreen) {
      targetScreen.classList.add('is-active');
    }

    // Update back button visibility (hide on first screen)
    const backBtn = targetScreen?.querySelector('.bif-back');
    if (backBtn) {
      backBtn.classList.toggle('is-hidden', this.currentScreenIndex === 0);
    }

    // Update button text for final screen
    const nextBtn = targetScreen?.querySelector('.bif-btn-text');
    if (nextBtn) {
      const isFinalScreen = screenId === 'finish';
      nextBtn.textContent = isFinalScreen ? 'Finish & Submit' : 'Next Step';
    }

    this.log(`Showing screen ${screenId}`);
  }

  private updateProgress(): void {
    if (!this.container) return;

    const progressFill = this.container.querySelector('.bif-progress-fill') as HTMLElement;
    if (progressFill) {
      const totalScreens = this.activeScreenIds.length;
      const percentage = ((this.currentScreenIndex + 1) / totalScreens) * 100;
      progressFill.style.width = `${percentage}%`;
    }
  }

  private updateSidebar(): void {
    if (!this.container) return;

    const currentScreenId = this.getCurrentScreenId();
    const screenConfig = this.getScreenConfig(currentScreenId);
    if (!screenConfig) return;

    const titleEl = this.container.querySelector('.bif-title');
    const subtitleEl = this.container.querySelector('.bif-subtitle');

    // Both overrides address the screen the visitor lands on, whichever flow
    // this container is in — `contact` for the picker, `li-contact` for a
    // listing. Later screens keep the copy their own config carries.
    const isFirstScreen = currentScreenId === this.activeScreenIds[0];
    const isTourFirstScreen = this.isTourContext && isFirstScreen;

    if (titleEl) {
      const titleText =
        isFirstScreen && this.titleOverride
          ? this.titleOverride
          : isTourFirstScreen
            ? TOUR_CONTEXT.title
            : screenConfig.title;
      titleEl.textContent = titleText;
    }

    if (subtitleEl) {
      const stackedSubtitle =
        'Provide a few details and we’ll follow up with next steps, lender options, and how to schedule a quick call.';
      const subtitleText =
        isFirstScreen && this.subtitleOverride
          ? this.subtitleOverride
          : isTourFirstScreen
            ? TOUR_CONTEXT.subtitle
            : this.isStackedLayout
              ? stackedSubtitle
              : screenConfig.subtitle;

      if (subtitleText) {
        subtitleEl.textContent = subtitleText;
        subtitleEl.classList.remove('is-hidden');
      } else {
        subtitleEl.textContent = '';
        subtitleEl.classList.add('is-hidden');
      }
    }
  }

  // ============================================
  // VALIDATION
  // ============================================

  private validateCurrentScreen(): boolean {
    if (!this.container) return false;

    const currentScreenId = this.getCurrentScreenId();
    const currentScreenEl = this.container.querySelector(`[data-screen="${currentScreenId}"]`);
    if (!currentScreenEl) return false;

    let isValid = true;

    // Clear previous errors
    currentScreenEl.querySelectorAll('.is-invalid').forEach((el) => {
      el.classList.remove('is-invalid');
    });

    // Validate visible text inputs and textareas
    const inputs = currentScreenEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      '.bif-input[required], .bif-textarea[required]'
    );

    inputs.forEach((input) => {
      // Skip if input is inside a hidden reveals section
      const revealsParent = input.closest('.bif-reveals');
      if (revealsParent && !revealsParent.classList.contains('is-visible')) {
        return;
      }

      if (!input.value.trim()) {
        isValid = false;
        input.classList.add('is-invalid');
      } else if (input.type === 'email' && !this.isValidEmail(input.value)) {
        isValid = false;
        input.classList.add('is-invalid');
      } else if (input.type === 'tel' && !this.isValidPhone(input.value)) {
        isValid = false;
        input.classList.add('is-invalid');
      }
    });

    // Validate visible required radio groups
    const radioGroups = currentScreenEl.querySelectorAll<HTMLFieldSetElement>('.bif-radio-group');

    radioGroups.forEach((fieldset) => {
      const groupName = fieldset.dataset.radioGroup;
      if (!groupName) return;

      // Skip if inside a hidden reveals section
      const revealsParent = fieldset.closest('.bif-reveals');
      if (revealsParent && !revealsParent.classList.contains('is-visible')) {
        return;
      }

      // Check if any option is selected
      const checkedInput = fieldset.querySelector<HTMLInputElement>('input[type="radio"]:checked');

      // If no checked and there's a required indicator in the config
      if (!checkedInput) {
        // Find the field config to check if required
        const fieldConfig = this.findFieldConfigByName(groupName);
        if (fieldConfig && (fieldConfig as RadioFieldConfig).required) {
          isValid = false;
          fieldset.classList.add('is-invalid');
        }
      }
    });

    this.log(`Validation result: ${isValid}`);
    return isValid;
  }

  private findFieldConfigByName(name: string, screens: ScreenConfig[] = this.config.screens): FieldConfig | null {
    for (const screen of screens) {
      for (const field of screen.fields) {
        if ('name' in field && field.name === name) {
          return field;
        }
        // Check inside reveals
        if (field.type === 'radio') {
          for (const option of field.options) {
            if (option.reveals) {
              for (const revealField of option.reveals) {
                if ('name' in revealField && revealField.name === name) {
                  return revealField;
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  private isValidPhone(phone: string): boolean {
    return this.normalizePhone(phone).length === 10;
  }

  // ============================================
  // RADIO CHANGE HANDLER
  // ============================================

  private handleRadioChange(radio: HTMLInputElement): void {
    const groupName = radio.name;
    const selectedValue = radio.value;

    // Update selected state on cards (JS fallback for :has)
    const allCardsInGroup = this.container?.querySelectorAll(
      `.bif-radio-card input[name="${groupName}"]`
    );
    allCardsInGroup?.forEach((input) => {
      const card = input.closest('.bif-radio-card');
      card?.classList.toggle('is-selected', (input as HTMLInputElement).checked);
    });

    // Clear validation error on group
    const fieldset = radio.closest('.bif-radio-group');
    fieldset?.classList.remove('is-invalid');

    // Handle reveals for this radio group
    this.updateReveals(groupName, selectedValue);

    this.log(`Radio changed: ${groupName} = ${selectedValue}`);
  }

  // ============================================
  // CONDITIONAL REVEALS
  // ============================================

  private updateReveals(groupName: string, selectedValue: string): void {
    if (!this.container) return;

    // Find all reveals for this group
    const allReveals = this.container.querySelectorAll<HTMLElement>(
      `.bif-reveals[data-reveals-for="${groupName}"]`
    );

    allReveals.forEach((reveal) => {
      const revealValue = reveal.dataset.revealsValue;
      const shouldShow = revealValue === selectedValue;

      reveal.classList.toggle('is-visible', shouldShow);

      // Clear values and validation in hidden reveals
      if (!shouldShow) {
        this.clearRevealFields(reveal);
      }
    });
  }

  private clearRevealFields(reveal: HTMLElement): void {
    // Clear text inputs and textareas
    const inputs = reveal.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      '.bif-input, .bif-textarea'
    );
    inputs.forEach((input) => {
      input.value = '';
      input.classList.remove('is-invalid');
    });

    // Clear radio selections
    const radios = reveal.querySelectorAll<HTMLInputElement>('.bif-radio-input');
    radios.forEach((radio) => {
      radio.checked = false;
    });

    // Clear selected state on cards
    const cards = reveal.querySelectorAll('.bif-radio-card');
    cards.forEach((card) => card.classList.remove('is-selected'));

    // Clear validation errors on radio groups
    const radioGroups = reveal.querySelectorAll('.bif-radio-group');
    radioGroups.forEach((group) => group.classList.remove('is-invalid'));

    // Hide nested reveals
    const nestedReveals = reveal.querySelectorAll('.bif-reveals');
    nestedReveals.forEach((nested) => {
      nested.classList.remove('is-visible');
      this.clearRevealFields(nested as HTMLElement);
    });
  }

  // ============================================
  // FORM SUBMISSION
  // ============================================

  private async handleSubmit(): Promise<void> {
    this.log('Submitting form...');

    const formData = this.collectFormData();
    this.log('Collected form data:', formData);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      this.log('Submission successful:', result);

      this.showThankYouScreen();
    } catch (error) {
      this.log('Submission error:', error);
      // For now, still show thank you screen even on error
      // In production, you might want to show an error message
      this.showThankYouScreen();
    }
  }

  private collectFormData(): FormData {
    if (!this.formElement) {
      return {} as FormData;
    }

    // Scope queries to only active screens to avoid duplicate field name conflicts
    const getValue = (name: string): string => {
      for (const screenId of this.activeScreenIds) {
        const screen = this.formElement?.querySelector(`[data-screen="${screenId}"]`);
        const input = screen?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          `[name="${name}"]`
        );
        if (!input) continue;

        // For radio buttons, find the checked one within this screen
        if (input.type === 'radio') {
          const checked = screen?.querySelector<HTMLInputElement>(
            `[name="${name}"]:checked`
          );
          if (checked?.value) return checked.value;
          continue;
        }

        if (input.value.trim()) return input.value.trim();
      }
      return '';
    };

    const getRadioValue = (name: string): string => {
      for (const screenId of this.activeScreenIds) {
        const screen = this.formElement?.querySelector(`[data-screen="${screenId}"]`);
        const checked = screen?.querySelector<HTMLInputElement>(
          `[name="${name}"]:checked`
        );
        if (checked?.value) return checked.value;
      }
      return '';
    };

    // Use stored userType or get from radio
    const userType = this.selectedUserType || getRadioValue('userType');
    const normalizedPhone = this.normalizePhone(getValue('phone'));

    const data: FormData = {
      // Common fields (all user types)
      firstName: getValue('firstName'),
      lastName: getValue('lastName'),
      email: getValue('email'),
      phone: normalizedPhone,
      userType,
      referral: getRadioValue('referral'),
      vision: getValue('vision') || undefined,
      referralOther: getValue('referralOther') || undefined,
      newsletter:
        this.formElement.querySelector<HTMLInputElement>('input[name="newsletter"]')?.checked ||
        undefined,
      urlPath: window.location.pathname,
    };

    // Homebuyer-specific fields
    if (userType === 'homebuyer') {
      data.budget = getRadioValue('budget') || undefined;
      data.financing = getRadioValue('financing') || undefined;
      data.landOwnership = getRadioValue('landOwnership') || undefined;
      data.timeline = getRadioValue('timeline') || undefined;
    }

    // Developer-specific fields
    if (userType === 'developer') {
      data.homeCount = getRadioValue('homeCount') || undefined;
      data.landOwnership = getRadioValue('landOwnership') || undefined;
      data.projectStage = getRadioValue('projectStage') || undefined;
      data.timeline = getRadioValue('timeline') || undefined;
    }

    // Listing inquiry fields
    if (userType === 'listing-inquiry') {
      if (this.listingAddress && this.listingLat !== null && this.listingLng !== null) {
        data.landAddress = {
          location: {
            lat: this.listingLat,
            lng: this.listingLng,
          },
          formatted_address: this.listingAddress,
        };
      }
    }

    // The newsletter checkbox outranks every page intent; the page
    // lives on in urlPath.
    const userIntent = data.newsletter
      ? 'join-newsletter'
      : this.userIntent ?? (this.isTourContext ? TOUR_CONTEXT.userIntent : null);
    if (userIntent) {
      data.userIntent = userIntent;
    }

    // Remove undefined values
    Object.keys(data).forEach((key) => {
      if (data[key as keyof FormData] === undefined) {
        delete data[key as keyof FormData];
      }
    });

    return data;
  }

  private showThankYouScreen(): void {
    if (!this.container) return;

    // Hide all screens
    const allScreens = this.container.querySelectorAll('.bif-screen');
    allScreens.forEach((screen) => screen.classList.remove('is-active'));

    // Show thank you screen
    const thankYouScreen = this.container.querySelector('[data-screen="thankyou"]');
    if (thankYouScreen) {
      thankYouScreen.classList.add('is-active');
    }

    // Hide sidebar
    const sidebar = this.container.querySelector('.bif-sidebar');
    sidebar?.classList.add('is-hidden');

    // Update progress to 100%
    const progressFill = this.container.querySelector('.bif-progress-fill') as HTMLElement;
    if (progressFill) {
      progressFill.style.width = '100%';
    }

    this.log('Showing thank you screen');
  }

  // ============================================
  // RENDER METHODS
  // ============================================

  private render(): void {
    if (!this.container) return;

    // Filter screens to only render those relevant to current context
    const screensToRender = this.getScreensToRender();

    const formClassName = this.isStackedLayout ? 'bif-form bif-form--stacked' : 'bif-form';

    const formHtml = `
      <form class="${formClassName}" novalidate>
        <div class="bif-sidebar">
          <div class="bif-progress">
            <div class="bif-progress-fill"></div>
          </div>
          <h2 class="bif-title">Contact Details</h2>
          <p class="bif-subtitle">Tell us a bit about yourself.</p>
        </div>
        <div class="bif-content">
          ${screensToRender.map((screen, index) => this.renderScreen(screen, index === 0)).join('')}
          ${this.renderThankYouScreen()}
        </div>
      </form>
    `;

    this.container.innerHTML = formHtml;
    this.log('Form rendered');
  }

  /**
   * Get screens to render based on selectedUserType.
   * If userType is known at init, only render screens for that type.
   * If userType is not yet known, render screens for selection flow.
   */
  private getScreensToRender(): ScreenConfig[] {
    if (this.selectedUserType) {
      // UserType known: only render screens for this user type
      return this.config.screens.filter((screen) => {
        if (!screen.userTypes) return true; // shared screens like 'finish'
        return screen.userTypes.includes(this.selectedUserType!);
      });
    } else {
      // UserType not yet known: render screens for selection flow (exclude pre-set flows like listing-inquiry)
      return this.config.screens.filter((screen) => {
        if (!screen.userTypes) return true; // shared screens
        // Exclude listing-inquiry screens (those are only for pre-set context)
        return !screen.userTypes.includes('listing-inquiry');
      });
    }
  }

  private renderScreen(screen: ScreenConfig, isFirstScreen: boolean = false): string {
    const activeClass = isFirstScreen ? ' is-active' : '';

    const fieldsHtml = this.renderFields(screen);

    // Button text will be updated dynamically in showScreen()
    const buttonText = 'Next Step';

    return `
      <div data-screen="${screen.id}" class="bif-screen${activeClass}">
        <div class="bif-fields">
          ${fieldsHtml}
        </div>
        <div class="bif-nav">
          <button type="button" class="bif-btn bif-btn-next">
            <span class="bif-btn-text">${buttonText}</span>
            <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" class="bif-btn-arrow">
              <path fill-rule="evenodd" d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z" fill="currentColor"></path>
            </svg>
          </button>
          <button type="button" class="bif-back${isFirstScreen ? ' is-hidden' : ''}">back</button>
        </div>
      </div>
    `;
  }

  private renderThankYouScreen(): string {
    return `
      <div data-screen="thankyou" class="bif-screen bif-screen-thankyou">
        <div class="bif-thankyou-content">
          <h3 class="bif-thankyou-title">Thank you!</h3>
          <p class="bif-thankyou-text">Your submission has been received.<br> We'll be in touch soon.</p>
        </div>
      </div>
    `;
  }

  private renderField(field: FieldConfig): string {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return this.renderTextInput(field);
      case 'textarea':
        return this.renderTextarea(field);
      case 'radio':
        return this.renderRadioGroup(field);
      case 'checkbox':
        return this.renderCheckbox(field);
      default:
        return '';
    }
  }

  private renderFields(screen: ScreenConfig): string {
    const fieldsHtml: string[] = [];
    const hasFirstName = screen.fields.some(
      (field) => 'name' in field && field.name === 'firstName'
    );
    const lastNameField = screen.fields.find(
      (field) => 'name' in field && field.name === 'lastName'
    );

    screen.fields.forEach((field) => {
      if ('name' in field && field.name === 'firstName' && hasFirstName && lastNameField) {
        fieldsHtml.push(
          `<div class="bif-field-row">${this.renderField(field)}${this.renderField(
            lastNameField
          )}</div>`
        );
        return;
      }

      if ('name' in field && field.name === 'lastName' && hasFirstName) {
        return;
      }

      fieldsHtml.push(this.renderField(field));
    });

    return fieldsHtml.join('');
  }

  private renderTextInput(field: TextFieldConfig): string {
    const requiredAttr = field.required ? 'required' : '';
    const inputHtml = `
      <input
        type="${field.type}"
        name="${field.name}"
        class="bif-input"
        placeholder="${field.placeholder}"
        ${requiredAttr}
      />
    `;

    if (!field.hint) {
      return inputHtml;
    }

    return `
      <div class="bif-input-wrap">
        ${inputHtml}
        <p class="bif-input-hint">${field.hint}</p>
      </div>
    `;
  }

  private renderCheckbox(field: CheckboxFieldConfig): string {
    return `
      <label class="bif-checkbox">
        <input type="checkbox" name="${field.name}" class="bif-checkbox-input" />
        <span class="bif-checkbox-label">${field.label}</span>
      </label>
    `;
  }

  private renderTextarea(field: TextareaFieldConfig): string {
    const requiredAttr = field.required ? 'required' : '';
    return `
      <textarea
        name="${field.name}"
        class="bif-textarea"
        placeholder="${field.placeholder}"
        rows="4"
        ${requiredAttr}
      ></textarea>
    `;
  }

  private renderRadioGroup(field: RadioFieldConfig): string {
    const questionHtml = field.question
      ? `<legend class="bif-question">${field.question}</legend>`
      : '';

    const optionsHtml = field.options
      .map((option) => this.renderRadioOption(field.name, option))
      .join('');

    return `
      <fieldset class="bif-radio-group" data-radio-group="${field.name}">
        ${questionHtml}
        ${optionsHtml}
      </fieldset>
    `;
  }

  private renderRadioOption(groupName: string, option: RadioOption): string {
    const revealsHtml = option.reveals
      ? `<div class="bif-reveals" data-reveals-for="${groupName}" data-reveals-value="${option.value}">
          ${option.reveals.map((f) => this.renderField(f)).join('')}
        </div>`
      : '';

    return `
      <label class="bif-radio-card">
        <input type="radio" name="${groupName}" value="${option.value}" class="bif-radio-input" />
        <span class="bif-radio-label">${option.label}</span>
        <span class="bif-radio-indicator"></span>
      </label>
      ${revealsHtml}
    `;
  }
}

export default BuyerIntakeForm;
